import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { uniqueBy } from 'remeda';
import { useAxiosInstance, useLegacyAxiosInstance } from 'shared/hooks/utils';
import type { TableFilter } from 'types/views';

import { getOptionLoader, type SelectOption } from '../filters/optionLoaders';

export type { SelectOption };

// Loads and caches the select options for api-backed filters, keyed by filter name.
// Each filter's fetch + serialise recipe lives in the optionLoaders registry; this
// hook owns the cached listData state. Functional state updates so concurrent loads
// merge rather than clobber.
export function useFilterOptionsLoader() {
  const queryClient = useQueryClient();
  const axiosInstance = useAxiosInstance();
  const legacyAxiosInstance = useLegacyAxiosInstance();
  const [listData, setListData] = useState<Record<string, SelectOption[]>>({});

  const loadOptions =
    (filter: TableFilter) =>
    async (search = '') => {
      const serialized = await getOptionLoader(filter.name)({
        queryClient,
        axiosInstance,
        legacyAxiosInstance,
        filter,
        search,
      });

      setListData(previous => ({
        ...previous,
        [filter.name]: uniqueBy(
          [...(previous[filter.name] ?? []), ...serialized],
          option => option.id,
        ),
      }));
    };

  return { listData, loadOptions };
}
